import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Permission, StaffService, StaffServiceEditResource, StaffServiceViewResource } from "generated/openApi";
import { useRouter } from "hooks";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StaffServicePageBody } from "./StaffServicePageBody/StaffServicePageBody";
import { StaffServicePageHeader } from "./StaffServicePageHeader";

const routeName = "staffService";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  staffId?: number = 0;
  hideStaff?: boolean = false;
}

export const staffServicePageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Service",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

const ROOT_PERMISSION: Permission = Permission.StaffService;

export const StaffServicePage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();

  const [hideStaff] = useState<boolean>(optionalParams.hideStaff || false);
  const [staffId] = useState<number | undefined>(optionalParams.staffId);

  const [service, setService] = useState<StaffService>({} as StaffService);
  const [initService, setInitService] = useState<StaffService>(service);

  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({
    trackedItems: [service],
  });
  const [viewResource, setViewResource] = useState<StaffServiceViewResource>({});
  const [editResource, setEditResource] = useState<StaffServiceEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(
      `../${staffServicePageRoute.getPath({
        id: id,
        crudMode: crudMode,
        screenType: params.screenType,
      })}`
    );

  const fetch = (id: number) => {
    ApiService.staffServices.get(id).then((response) => {
      if (!id && staffId) response.staffId = staffId;

      setService(response);
      setInitService(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.staffServices.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.staffServices.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StaffServicePageHeader
      rootPermission={ROOT_PERMISSION}
      handle={staffServicePageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      service={service}
      setService={setService}
      initService={initService}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
      resetUnsavedChanges={resetUnsavedChanges}
      hideStaff={hideStaff}
    />
  );

  const pageBody = (
    <StaffServicePageBody
      rootPermission={ROOT_PERMISSION}
      crudMode={params.crudMode}
      service={service}
      setService={setService}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
      hideStaff={hideStaff}
      onEdit={() => reroute(params.id, "edit")}
    />
  );

  return (
    <>
      {params.screenType === "standard" ? (
        <Page isForm header={pageHeader}>
          <Card>{pageBody}</Card>
        </Page>
      ) : (
        <Dialog
          isForm
          open={true}
          onClose={() => handleClose(navigate, unsavedChanges)}
          maxWidth="lg"
          header={pageHeader}
          content={pageBody}
        />
      )}
    </>
  );
};
